import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronRight, Copy, Facebook, FileText, Instagram, RefreshCw, Sparkles, Video, Wand2, XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared internally — renders the structured payload of an AI-generated
// social/article entry. Used by the "View generation" modal below.
function GenerationViewer({ entry }) {
  const p = entry.payload || {};
  if (entry.gen_type === 'article') {
    return (
      <div className="space-y-3">
        <div>
          <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wide">Title</p>
          <h3 className="text-lg font-bold text-gray-900">{p.title}</h3>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><p className="text-[10px] uppercase text-gray-500 font-semibold">Slug</p><p className="font-mono text-gray-700">{p.slug}</p></div>
          <div><p className="text-[10px] uppercase text-gray-500 font-semibold">Tags</p><p className="text-gray-700">{(p.tags || []).join(', ')}</p></div>
        </div>
        <div>
          <p className="text-[10px] uppercase text-gray-500 font-semibold">Meta Description</p>
          <p className="text-gray-700 text-xs italic">{p.meta_description}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-gray-500 font-semibold">Markdown ({(p.markdown || '').length} chars)</p>
          <pre className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-[11px] whitespace-pre-wrap max-h-80 overflow-auto">{p.markdown}</pre>
        </div>
        {p.faqs && p.faqs.length > 0 && (
          <div>
            <p className="text-[10px] uppercase text-gray-500 font-semibold">FAQs</p>
            {p.faqs.map((f) => (
              <details key={f.question} className="border border-gray-100 rounded-lg my-1 px-3 py-2 text-xs">
                <summary className="font-semibold cursor-pointer">{f.question}</summary>
                <p className="text-gray-600 mt-1">{f.answer}</p>
              </details>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (entry.gen_type === 'carousel') {
    return (
      <div className="space-y-3">
        <div>
          <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wide">Caption</p>
          <pre className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-[11px] whitespace-pre-wrap">{p.caption}</pre>
        </div>
        <div>
          <p className="text-[10px] uppercase text-gray-500 font-semibold">Slides ({(p.slides || []).length})</p>
          <div className="space-y-2">
            {(p.slides || []).map(s => (
              <div key={s.slide} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] bg-pink-50 text-pink-700 px-1.5 py-0.5 rounded font-semibold">Slide {s.slide}</span>
                  <h4 className="font-semibold text-sm">{s.headline}</h4>
                </div>
                {s.body && <p className="text-xs text-gray-600">{s.body}</p>}
                {s.alt && <p className="text-[10px] text-gray-400 italic mt-1">Alt: {s.alt}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  // script_shotlist
  return (
    <div className="space-y-3">
      <div>
        <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wide">Hook</p>
        <p className="font-bold text-gray-900">&quot;{p.hook}&quot;</p>
      </div>
      <div>
        <p className="text-[10px] uppercase text-gray-500 font-semibold">Voiceover</p>
        <pre className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-[11px] whitespace-pre-wrap">{p.voiceover}</pre>
      </div>
      <div>
        <p className="text-[10px] uppercase text-gray-500 font-semibold">Shotlist ({(p.shots || []).length})</p>
        <div className="space-y-2">
          {(p.shots || []).map(s => (
            <div key={s.shot} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-semibold">Shot {s.shot}</span>
                <span className="text-[10px] text-gray-500">{s.duration_sec}s · {s.camera_angle}</span>
              </div>
              <p className="text-xs text-gray-700"><b>Visual:</b> {s.visual}</p>
              {s.on_screen_text && <p className="text-xs text-gray-700 mt-1"><b>On-screen:</b> {s.on_screen_text}</p>}
            </div>
          ))}
        </div>
      </div>
      {p.cta && (
        <div>
          <p className="text-[10px] uppercase text-gray-500 font-semibold">CTA</p>
          <p className="text-sm">{p.cta}</p>
        </div>
      )}
      {p.music_brief && (
        <div>
          <p className="text-[10px] uppercase text-gray-500 font-semibold">Music Brief</p>
          <p className="text-xs text-gray-600 italic">{p.music_brief}</p>
        </div>
      )}
    </div>
  );
}

export default function MetaVaultTab() {
  const token = sessionStorage.getItem('admin_token');
  const [status, setStatus] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'instagram' | 'facebook' | 'top'
  const [generating, setGenerating] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [viewerGen, setViewerGen] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === 'instagram' || filter === 'facebook') params.set('platform', filter);
      if (filter === 'top') params.set('top_only', 'true');
      params.set('limit', '50');
      const [s, p] = await Promise.all([
        fetch(`${API_URL}/api/admin/meta/status`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/meta/posts?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setStatus(s);
      setPosts(p.items || []);
    } catch (err) { console.error('Load Meta Vault failed:', err); } finally { setLoading(false); }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const manualSync = async () => {
    setSyncing(true);
    try {
      await fetch(`${API_URL}/api/admin/meta/sync`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      await load();
    } catch (err) { console.error('Manual Meta sync failed:', err); } finally { setSyncing(false); }
  };

  const generate = async (postId, genType) => {
    setGenerating(`${postId}-${genType}`);
    try {
      const res = await fetch(`${API_URL}/api/admin/meta/posts/${postId}/generate?gen_type=${genType}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        alert(`Generation failed: ${e.detail || res.status}`);
      } else {
        const entry = await res.json();
        setViewerGen(entry);
        await load();
      }
    } catch (err) { alert(`Network error: ${err.message}`); }
    finally { setGenerating(''); }
  };

  const copyText = (text) => {
    try { navigator.clipboard.writeText(text); } catch (err) { console.error('Clipboard write failed:', err); }
  };

  if (loading) return <div className="text-center py-12 text-gray-400" data-testid="metavault-loading">Loading Content Vault…</div>;

  const credsOk = status?.credentials_configured && Object.values(status.credentials_configured).every(Boolean);

  return (
    <div className="space-y-6" data-testid="metavault-tab">
      {/* Status banner */}
      <div className={`rounded-xl border p-4 ${status?.client_mode === 'live' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="metavault-status-banner">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status?.client_mode === 'live' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <Sparkles className={`w-5 h-5 ${status?.client_mode === 'live' ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Content Vault — {status?.client_mode === 'live' ? 'LIVE' : 'MOCK MODE'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {credsOk
                  ? `Last synced ${status?.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : '—'} · ${status?.total_posts} posts · ${status?.top_decile_posts} in top decile`
                  : 'Add Meta tokens to /app/backend/.env (META_IG_LONG_LIVED_TOKEN, INSTAGRAM_USER_ID, META_FB_PAGE_ACCESS_TOKEN, FACEBOOK_PAGE_ID) then restart backend.'}
              </p>
            </div>
          </div>
          <button onClick={manualSync} disabled={syncing}
            className="px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-black disabled:opacity-50 flex items-center gap-1.5"
            data-testid="metavault-sync-btn">
            {syncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Manual Sync
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Posts', val: status?.total_posts ?? 0, color: 'text-blue-600' },
          { label: 'Instagram', val: status?.instagram_posts ?? 0, color: 'text-pink-600' },
          { label: 'Facebook', val: status?.facebook_posts ?? 0, color: 'text-indigo-600' },
          { label: 'Top Decile', val: status?.top_decile_posts ?? 0, color: 'text-emerald-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'all', label: `All (${status?.total_posts ?? 0})` },
          { key: 'top', label: `Top 10% (${status?.top_decile_posts ?? 0})` },
          { key: 'instagram', label: `Instagram (${status?.instagram_posts ?? 0})` },
          { key: 'facebook', label: `Facebook (${status?.facebook_posts ?? 0})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${filter === f.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`metavault-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Post Cards */}
      {posts.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-100" data-testid="metavault-empty">
          No posts match this filter. {status?.client_mode === 'mock' && 'Mock data should populate after Manual Sync.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {posts.map(p => {
            const m = p.engagement_metrics || {};
            const er = p.engagement_rate;
            const PlatformIcon = p.platform === 'instagram' ? Instagram : Facebook;
            const isOpen = expanded === p.platform_post_id;
            return (
              <div key={p.platform_post_id}
                className={`bg-white rounded-xl border ${p.is_top_decile ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-gray-100'} overflow-hidden`}
                data-testid={`metavault-card-${p.platform_post_id}`}>
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {p.media_url && (
                      <img src={p.media_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <PlatformIcon className={`w-3.5 h-3.5 ${p.platform === 'instagram' ? 'text-pink-500' : 'text-indigo-500'}`} />
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">{p.platform}</span>
                        <span className="text-[10px] text-gray-400">·</span>
                        <span className="text-[10px] text-gray-400">{p.media_type}</span>
                        {p.is_top_decile && (
                          <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">TOP 10%</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-700 leading-snug line-clamp-3" data-testid={`metavault-caption-${p.platform_post_id}`}>
                        {p.caption || <em className="text-gray-400">No caption</em>}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
                        <span>❤ {m.likes ?? 0}</span>
                        <span>💬 {m.comments ?? 0}</span>
                        {p.platform === 'instagram' && <span>🔖 {m.saves ?? 0}</span>}
                        {p.platform === 'facebook' && <span>↗ {m.shares ?? 0}</span>}
                        <span>👁 {m.reach ?? '—'} reach</span>
                        <span className={`font-bold ${er > 0.1 ? 'text-emerald-600' : 'text-gray-700'}`}>
                          ER: {er != null ? `${(er * 100).toFixed(2)}%` : '—'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action row */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                    <button onClick={() => generate(p.platform_post_id, 'article')} disabled={!!generating}
                      className="px-2.5 py-1.5 bg-purple-50 text-purple-700 text-[10px] font-semibold rounded-lg hover:bg-purple-100 disabled:opacity-50 flex items-center gap-1"
                      data-testid={`metavault-gen-article-${p.platform_post_id}`}>
                      {generating === `${p.platform_post_id}-article` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                      Blog Article
                    </button>
                    <button onClick={() => generate(p.platform_post_id, 'carousel')} disabled={!!generating}
                      className="px-2.5 py-1.5 bg-pink-50 text-pink-700 text-[10px] font-semibold rounded-lg hover:bg-pink-100 disabled:opacity-50 flex items-center gap-1"
                      data-testid={`metavault-gen-carousel-${p.platform_post_id}`}>
                      {generating === `${p.platform_post_id}-carousel` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Instagram className="w-3 h-3" />}
                      Carousel
                    </button>
                    <button onClick={() => generate(p.platform_post_id, 'script_shotlist')} disabled={!!generating}
                      className="px-2.5 py-1.5 bg-amber-50 text-amber-700 text-[10px] font-semibold rounded-lg hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1"
                      data-testid={`metavault-gen-script-${p.platform_post_id}`}>
                      {generating === `${p.platform_post_id}-script_shotlist` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Video className="w-3 h-3" />}
                      Script + Shots
                    </button>
                    {p.permalink && (
                      <a href={p.permalink} target="_blank" rel="noopener noreferrer"
                        className="ml-auto text-[10px] text-gray-400 hover:text-gray-700">View original →</a>
                    )}
                  </div>

                  {/* Generations list */}
                  {(p.generations || []).length > 0 && (
                    <button onClick={() => setExpanded(isOpen ? null : p.platform_post_id)}
                      className="mt-3 text-[10px] text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
                      data-testid={`metavault-toggle-gens-${p.platform_post_id}`}>
                      <Wand2 className="w-3 h-3" />
                      {isOpen ? 'Hide' : 'Show'} {(p.generations || []).length} generation(s)
                    </button>
                  )}
                  {isOpen && (p.generations || []).length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {(p.generations || []).slice().reverse().map(g => (
                        <button key={g.id} onClick={() => setViewerGen(g)}
                          className="w-full text-left px-2.5 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center gap-2"
                          data-testid={`metavault-gen-entry-${g.id}`}>
                          <span className="text-[10px] bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-600 font-semibold uppercase">{g.gen_type.replace('_', '+')}</span>
                          <span className="text-[10px] text-gray-500">{new Date(g.created_at).toLocaleString()}</span>
                          <ChevronRight className="w-3 h-3 text-gray-400 ml-auto" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Generation Viewer Modal */}
      {viewerGen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setViewerGen(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="metavault-viewer">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs bg-gray-100 px-2 py-1 rounded font-semibold uppercase">{viewerGen.gen_type.replace('_', '+')}</span>
                <span className="text-xs text-gray-500">{new Date(viewerGen.created_at).toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => copyText(JSON.stringify(viewerGen.payload, null, 2))}
                  className="px-2.5 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 rounded font-semibold flex items-center gap-1" data-testid="metavault-copy-json">
                  <Copy className="w-3 h-3" /> Copy JSON
                </button>
                <button onClick={() => setViewerGen(null)} className="text-gray-500 hover:text-black" data-testid="metavault-viewer-close">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-5 py-4 overflow-auto flex-1 text-sm">
              <GenerationViewer entry={viewerGen} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
